// Which city this app is showing.
//
// Late Again was written for Melbourne and its shape was written into the code:
// the loop and the tunnel as a hard-coded list of eleven station names, the
// through-running pairs as route ids, "Metro Trains Melbourne" in the prose,
// PTV in every error. None of that is wrong — it is just not general, and a
// second city cannot be added by adding a second bundle while the code still
// believes there is one.
//
// So everything the app knows about a city that is NOT in its network bundle
// lives here, as data, in one table. The bundle says where the stations are and
// what order they come in; this says what to call the operator, which stations
// count as "the city", which lines run through it, and what to put on the end
// of an API call.
//
// IMPORT-FREE, like `src/rules.js`, and for the same reason: it is read during
// the launch window, before anything has been hydrated, and by modules
// (`src/network.js`) that must not acquire a dependency on Vue or the store.
// That is also why it reads localStorage directly rather than through the
// `prefs` proxy in `core/storage.js` — the key below is the identical key
// `prefs['lateagain:city:v1']` reads and writes, prefix and JSON encoding
// included, so the two are the same value seen through two windows.

/** The pref the `prefs` proxy would write, spelled out. */
const KEY = 'pref:lateagain:city:v1';

/**
 * Is this `/demo`? Tested here rather than imported.
 *
 * `src/core/demo.js` owns this question everywhere else, but it holds the
 * answer in a Vue ref and this file is import-free by the note above — it is
 * read during the launch window and by `src/network.js`, which must not
 * acquire Vue. So the test is repeated, and it is the same one line.
 *
 * It matters because this file is the one place that deliberately goes round
 * the `prefs` proxy, and the proxy is what makes a demo leave nothing behind.
 * Without this, somebody switching to Sydney inside the demo would find the
 * REAL app opening in Sydney afterwards — a stranger's tap changing an install
 * they never opened. The demo starts in Melbourne and keeps its choice in
 * memory, like everything else it does.
 */
const IS_DEMO = typeof location !== 'undefined'
  && /^\/(app\/)?demo(\/|$)/.test(location.pathname);

/** Melbourne, because that is the app that exists and the one people have. */
export const DEFAULT_CITY = 'melbourne';

/**
 * Every city this app can show, and everything about one that the network
 * bundle does not carry.
 *
 * `apiCity` is the only field the server sees. Melbourne is `null` rather than
 * `'melbourne'` on purpose: the endpoints have answered for Melbourne since the
 * first build and a request with no `city` still means Melbourne, so nothing
 * that is already deployed has to change for Sydney to arrive.
 *
 * The rest is what used to be scattered:
 *
 *   `cityEnd`     the stations the strip collapses into one "the city" block,
 *                 by name. Melbourne's is the loop, the Metro Tunnel and North
 *                 Melbourne, where the western lines fan out; Sydney's is the
 *                 CBD underground and Redfern.
 *   `through`     lines whose trains continue past the city as another line,
 *                 both directions. Sydney's is EMPTY and that is the honest
 *                 answer, not a gap: Sydney Trains runs the City Circle and
 *                 lines keep their own name right through it.
 *   `walkLinks`   city stations that are one interchange joined by a footway
 *                 and two stop ids. Melbourne has two such pairs under the
 *                 tunnel; Sydney, in this spike, has none.
 *   `reserved`    lines whose seats are booked rather than boarded. Melbourne's
 *                 five long-distance V/Line services; every other city's is
 *                 absent, which reads as none.
 *   `change`      the least time the planner will leave somebody to change
 *                 trains: `{ default, stations }`, in minutes, read by
 *                 `changeMinutes` in journey.js. KEYED BY NAME, normalised the
 *                 way `normaliseStationName` normalises — a stop id would be
 *                 the one thing in this file that a rebuilt bundle could
 *                 silently invalidate, and a table that had quietly stopped
 *                 matching would read as a planner that had gone back to
 *                 offering one-minute changes.
 *                 A station absent from `stations` takes `default`, which is
 *                 the ordinary cross-platform change: off one train, along the
 *                 platform, onto the next. The named ones are the places that
 *                 is not true of — a concourse, a set of stairs, or a platform
 *                 at the other end of the station.
 *   `centre`      one coordinate that stands for the whole network, used by
 *                 `nearestCity` below and by nothing else. It is the city's
 *                 own central station rather than a centroid of the lines: a
 *                 centroid would sit wherever the longest line happens to run
 *                 and would put "Melbourne" somewhere out past Sunshine.
 *   `defaultStopId` where the board opens when there is nothing saved.
 *   `suggested`   the handful of stations offered before anyone has searched.
 *   `hasAlerts`   whether this network publishes disruption notices AT ALL.
 *                 Four of the five do. Transport Canberra does not — there is
 *                 no service-alerts endpoint in the ACT feed and the combined
 *                 file carries zero alert entities — so its ledger is honestly
 *                 always "running to timetable", and the Lines and Alerts tabs
 *                 say so in as many words rather than drawing an empty list
 *                 that reads exactly like a feed that has fallen over. A flag
 *                 rather than a count, because "no alerts today" and "no alerts
 *                 ever" are different sentences and only the data can tell them
 *                 apart. Perth publishes none. Canberra's light rail publishes
 *                 none either, but the city's NSW TrainLink trains do, so it
 *                 says `true` and carries the sentence about the silent
 *                 line in `quietNote`.
 *   `live`        whether this network publishes ANYTHING live — departure
 *                 estimates, train positions, cancellations. Five of the six
 *                 do. Perth does not: its only open data is the timetable, so
 *                 every time on its boards is the scheduled one, no train is
 *                 ever drawn on a strip or the map, and the screens that would
 *                 otherwise say "departure times and train positions are live"
 *                 say the opposite. Absent reads as true, so the five that were
 *                 here first do not have to say it.
 *
 * `covers` is what that city's feed actually carries, in the order a reader
 * would name it. It is not the operator: PTV's feed is Metro Trains AND
 * V/Line, Sydney's is four networks under one licence, and a row that named
 * only the biggest one read as though the rest were missing.
 *
 * `sourceShort` exists because the source appears in running copy — "Couldn't
 * reach PTV" is on five screens — and "Couldn't reach Public Transport
 * Victoria" is not a sentence anybody in Melbourne says.
 */
export const CITIES = {
  melbourne: {
    name: 'Melbourne',
    tz: 'Australia/Melbourne',
    operator: 'Metro Trains Melbourne',
    covers: ['Metro Trains', 'V/Line'],
    // What the Lines tab calls its train groups; any other city says TRAINS
    // and REGIONAL (src/line-groups.js).
    lineGroups: { train: 'METRO', regional: 'V/LINE' },
    source: 'Public Transport Victoria',
    sourceShort: 'PTV',
    apiCity: null,
    licence: 'Licensed from Public Transport Victoria under a Creative Commons '
      + 'Attribution 4.0 International Licence.',
    disclaimer: 'Late Again is not affiliated with, endorsed by or associated with '
      + 'Public Transport Victoria, the Victorian Department of Transport and '
      + 'Planning, or Metro Trains Melbourne.',
    hasAlerts: true,
    mapUrl: 'https://www.metrotrains.com.au/maps/',
    disruptionsUrl: 'https://www.ptv.vic.gov.au/live-travel-updates/',
    centre: { lat: -37.8183, lng: 144.9671 },   // Flinders Street
    defaultStopId: 1071,   // Flinders Street
    suggested: ['Flinders Street', 'Richmond', 'Southern Cross', 'Melbourne Central'],
    // The loop, the Metro Tunnel, and North Melbourne.
    cityEnd: [
      'Parliament', 'State Library', 'Town Hall', 'Anzac', 'Flinders Street',
      'Southern Cross', 'Melbourne Central', 'Flagstaff', 'Parkville', 'Arden',
      'North Melbourne',
    ],
    // Since 1 February 2026 the tunnel carries Sunbury trains through to become
    // Cranbourne and Pakenham services; from mid-2026 Sandringham runs through
    // to Werribee and Williamstown. Both directions, because the strip is drawn
    // from either end.
    through: {
      4: [14],        // Cranbourne   → Sunbury
      11: [14],       // Pakenham     → Sunbury
      14: [11, 4],    // Sunbury      → Pakenham, Cranbourne
      12: [16, 17],   // Sandringham  → Werribee, Williamstown
      16: [12],       // Werribee     → Sandringham
      17: [12],       // Williamstown → Sandringham
    },
    walkLinks: [
      [1234, 1120],   // State Library ↔ Melbourne Central
      [1235, 1071],   // Town Hall     ↔ Flinders Street
    ],
    // The V/Line lines you book a seat on rather than turn up for. The long-
    // distance services — every one of them runs a handful of times a day,
    // past the myki zones, in carriages sold by the seat. The commuter lines
    // (Geelong, Ballarat, Bendigo, Traralgon, Seymour) are NOT here and must
    // not be: they are turn-up-and-go and a reservation note on the 7:41 to
    // Geelong would be the app inventing a barrier.
    //
    // Route ids rather than names, because that is what a leg carries. Read by
    // `needsReservation` in journey.js, which puts one line on the trip card.
    // How long a change really takes here.
    //
    // A cross-platform change on the metropolitan network is quick — Caulfield
    // and Richmond put the next train on the other face of the same island —
    // and three minutes is honest about it. The named stations are the ones
    // where it is not a step across:
    //
    //   Southern Cross   the biggest concourse in the state, with the V/Line
    //                    platforms at the far end of it and a barrier line
    //                    between them and the suburban ones.
    //   Flinders Street  fourteen platforms off one subway, and the subway is
    //                    the only way between them.
    //   Footscray        the V/Line platforms are a separate island reached by
    //                    the overpass, which is the change Carnegie to Lara
    //                    actually makes.
    //   Sunshine         same shape as Footscray, and the regional platforms
    //                    are the far pair.
    //   North Melbourne  six platforms on two levels joined by one ramp.
    //   Dandenong        the Gippsland platform is not the suburban one.
    //
    // Richmond and Caulfield are named at the default rather than left out, so
    // that a reader can see they were considered and found to be ordinary.
    change: {
      default: 3,
      stations: {
        'Southern Cross': 6,
        'Flinders Street': 5,
        Footscray: 4,
        Sunshine: 4,
        Richmond: 3,
        Caulfield: 3,
        Dandenong: 4,
        'North Melbourne': 4,
      },
    },
    reserved: [
      1706,   // Albury
      1823,   // Bairnsdale
      1848,   // Swan Hill
      1853,   // Warrnambool
      1908,   // Shepparton
    ],
  },

  sydney: {
    name: 'Sydney',
    tz: 'Australia/Sydney',
    operator: 'Sydney Trains',
    covers: ['Sydney Trains', 'Metro', 'NSW TrainLink', 'Light rail'],
    source: 'Transport for NSW',
    sourceShort: 'TfNSW',
    apiCity: 'sydney',
    // The Open Data Hub's own data-licence page: CC BY 4.0, "on the condition
    // that Transport for NSW is attributed as the source".
    licence: 'Data from Transport for NSW, used under a Creative Commons '
      + 'Attribution 4.0 International Licence.',
    disclaimer: 'Late Again is not affiliated with, endorsed by or associated with '
      + 'Transport for NSW or Sydney Trains.',
    hasAlerts: true,
    // Sydney Trains publishes its network map as a PDF off the TfNSW site.
    mapUrl: 'https://transportnsw.info/routes/train',
    disruptionsUrl: 'https://transportnsw.info/alerts/train',
    centre: { lat: -33.8832, lng: 151.2069 },   // Central
    defaultStopId: 200060,   // Central
    suggested: ['Central', 'Town Hall', 'Wynyard', 'Redfern'],
    // The CBD underground, plus Redfern — the station every western and
    // southern line passes through on the way in, and the one Sydney treats as
    // the edge of town the way Melbourne treats North Melbourne.
    cityEnd: [
      'Central', 'Town Hall', 'Wynyard', 'Circular Quay', 'St James',
      'Museum', 'Martin Place', 'Kings Cross', 'Redfern',
    ],
    // None. A T1 train is a T1 train all the way round the City Circle, so
    // there is no continuation to draw and nothing to be missing.
    through: {},
    walkLinks: [],
    // Central is the whole network's interchange and it is a long one — the
    // suburban platforms, the intercity ones and the country concourse are
    // three different places under one roof. Town Hall and Strathfield are
    // multi-level; Redfern and Chatswood are cross-platform for most of what
    // passes through them.
    change: {
      default: 3,
      stations: {
        Central: 6,
        'Town Hall': 4,
        Redfern: 3,
        Strathfield: 4,
        Chatswood: 3,
      },
    },
    // NSW TrainLink's regional trains: every seat is booked. The intercity
    // lines are not here and must not be — they are Opal, turn up and go.
    reserved: [
      1151,   // North Coast
      1152,   // North Western
      1153,   // Southern
      1154,   // Sydney–Canberra
      1155,   // Western
    ],
  },

  brisbane: {
    name: 'Brisbane',
    // QUEENSLAND HAS NO DAYLIGHT SAVING. This is UTC+10 every day of the year,
    // so for four months Brisbane and Sydney are an hour apart and for eight
    // they are not. Copying Sydney's zone here would put every Brisbane time
    // an hour out from October to April.
    tz: 'Australia/Brisbane',
    operator: 'Queensland Rail',
    covers: ['Queensland Rail', 'G:link tram'],
    source: 'Translink',
    sourceShort: 'Translink',
    apiCity: 'brisbane',
    // Translink's open-data page states it in one sentence: "Our data is
    // licensed under a Creative Commons Attribution 4.0 International License."
    licence: 'Data © Translink and the Queensland Government, used under a '
      + 'Creative Commons Attribution 4.0 International Licence.',
    disclaimer: 'Late Again is not affiliated with, endorsed by or associated with '
      + 'Translink, the Queensland Department of Transport and Main Roads, or '
      + 'Queensland Rail.',
    hasAlerts: true,
    mapUrl: 'https://translink.com.au/travel-with-us/timetables/train',
    disruptionsUrl: 'https://translink.com.au/service-updates',
    centre: { lat: -27.4661, lng: 153.0260 },   // Central
    defaultStopId: 600016,   // Central
    suggested: ['Central', 'Roma Street', 'South Brisbane', 'Fortitude Valley'],
    // Brisbane's city end is a CORRIDOR rather than a terminus — nearly every
    // line runs through Bowen Hills, Fortitude Valley, Central, Roma Street and
    // on across the river — which is why these are the stations the strip
    // collapses. South Bank and South Brisbane are in because the Airport,
    // Doomben and Ferny Grove lines reach town from that side.
    cityEnd: [
      'Bowen Hills', 'Fortitude Valley', 'Central', 'Roma Street',
      'South Brisbane', 'South Bank', 'Dutton Park', 'Boggo Road',
    ],
    // EMPTY HERE, AND FULL IN THE BUNDLE. Queensland Rail through-runs
    // constantly — a Ferny Grove train becomes a Beenleigh train at Bowen Hills
    // — and the first version of this table was empty because nothing said so.
    // Something does: the route short name is TWO TERMINUS CODES, origin then
    // destination, so `FGBN` is Ferny Grove to Beenleigh. The build script reads
    // the pairs off those codes (`deriveThrough` in scripts/fetch-network-gtfs.mjs,
    // for trips that really cross the city, and enough of them to be a service)
    // and writes them to `network.brisbane.json` as `through`, which
    // `throughPairs` in src/network.js merges with this. Nothing is written by
    // hand: a table from the network map would assert what the data cannot
    // confirm, and one read from the codes follows the timetable when it changes.
    through: {},
    // None. Brisbane's interchanges are all within one station.
    walkLinks: [],
    // Roma Street is where the long-distance and Gold Coast platforms sit
    // apart from the suburban ones, and Central is two narrow underground
    // platforms up a single set of escalators. Everything else on this network
    // changes across a platform.
    change: {
      default: 3,
      stations: {
        'Roma Street': 5,
        Central: 4,
      },
    },
  },

  adelaide: {
    name: 'Adelaide',
    tz: 'Australia/Adelaide',
    operator: 'Adelaide Metro',
    covers: ['Adelaide Metro', 'Glenelg tram'],
    source: 'Adelaide Metro',
    sourceShort: 'Adelaide Metro',
    apiCity: 'adelaide',
    // THIS ONE WAS A GUESS and said no licence at all, which is the one thing
    // an attribution may not do. The feed itself (gtfs.adelaidemetro.com.au) is
    // a bare Swagger page with no terms on it; the licence lives on the dataset
    // record at data.sa.gov.au, which names Creative Commons Attribution 4.0
    // and the Department for Infrastructure and Transport as the publisher.
    licence: 'Data from Adelaide Metro, Department for Infrastructure and '
      + 'Transport (South Australia), used under a Creative Commons '
      + 'Attribution 4.0 International Licence.',
    disclaimer: 'Late Again is not affiliated with, endorsed by or associated with '
      + 'Adelaide Metro or the South Australian Department for Infrastructure '
      + 'and Transport.',
    hasAlerts: true,
    mapUrl: 'https://www.adelaidemetro.com.au/plan-a-trip/network-maps',
    disruptionsUrl: 'https://www.adelaidemetro.com.au/service-disruptions',
    centre: { lat: -34.9209, lng: 138.5990 },   // Adelaide Railway Station
    defaultStopId: 6665,   // Adelaide Railway Station
    suggested: ['Adelaide', 'Adelaide Showground', 'Bowden', 'Mile End'],
    // Adelaide has one city station and every train line starts there, so "the
    // city" is nearly a single point. North Adelaide and Mile End are the first
    // stop out on the northern and western sides and belong with it the way
    // North Melbourne does. The tram's own city stops are here too: it runs
    // through town rather than terminating in it.
    cityEnd: [
      'Adelaide', 'Adelaide Railway Station Tram Stop', 'North Adelaide',
      'Mile End', 'Royal Adelaide Hospital', 'Rundle Mall', 'Victoria Square',
    ],
    // None. Every Adelaide line terminates in the city and keeps its own name.
    through: {},
    // None. The tram stop outside Adelaide Railway Station is its own station
    // and a few metres' walk, which is close enough to be worth a link — but it
    // is a footpath crossing, not a paid-area interchange, and this list is
    // about interchanges. Left out rather than invented.
    walkLinks: [],
    // No named stations, and that is the answer rather than a gap. Every
    // Adelaide line terminates at Adelaide station, so a change between two of
    // them is made on the same concourse; three minutes covers it, and naming
    // Adelaide at four would be inventing a distance nobody walks.
    change: { default: 3 },
  },

  canberra: {
    name: 'Canberra',
    // The ACT keeps NSW time, daylight saving included — the feed's own
    // `agency_timezone` says `Australia/Sydney`, and that is what is recorded
    // rather than the `Australia/Canberra` link to the same zone.
    tz: 'Australia/Sydney',
    operator: 'Canberra Metro',
    covers: ['Canberra Metro light rail', 'NSW TrainLink'],
    // The light rail is what Canberra's Lines tab is for; the one NSW TrainLink
    // service is the other group.
    lineGroups: { main: 'lightrail' },
    source: 'Transport Canberra',
    sourceShort: 'Transport Canberra',
    apiCity: 'canberra',
    // Transport Canberra's developer page says "Creative Common BY license"
    // and links the 4.0 legal code, so 4.0 is what it is.
    licence: 'Data © Transport Canberra, ACT Government, used under a Creative '
      + 'Commons Attribution 4.0 International Licence. NSW TrainLink data from '
      + 'Transport for NSW, used under a Creative Commons Attribution 4.0 '
      + 'International Licence.',
    disclaimer: 'Late Again is not affiliated with, endorsed by or associated with '
      + 'Transport Canberra, the ACT Government, Canberra Metro, Transport for NSW '
      + 'or NSW TrainLink.',
    // TRUE SINCE THE XPLORER ARRIVED, AND ONLY FOR IT. Transport Canberra
    // publishes no service alerts — no endpoint, zero alert entities — so the
    // light rail still never reads as anything but running to timetable. But
    // Canberra station's NSW TrainLink trains come from TfNSW, which does
    // publish them, so the city as a whole has notices and the Alerts tab is a
    // real list. The light rail's silence is said once, under the ledger, by
    // `quietNote`.
    hasAlerts: true,
    quietNote: 'Canberra Metro does not publish disruption notices, so the light '
      + 'rail will always read as running to timetable. The Sydney–Canberra '
      + 'trains’ notices come from Transport for NSW.',
    mapUrl: 'https://www.transport.act.gov.au/getting-around/light-rail',
    disruptionsUrl: 'https://www.transport.act.gov.au/news',
    centre: { lat: -35.2784, lng: 149.1300 },   // Alinga Street
    defaultStopId: 8128,   // Alinga Street
    suggested: ['Alinga Street', 'Gungahlin Place', 'Dickson Interchange', 'EPIC and Racecourse'],
    // Alinga Street is the city terminus and the two stops before it are still
    // town. Short, because the line is.
    cityEnd: ['Alinga Street', 'Elouera Street', 'Ipima Street'],
    // None, and there is nothing to be missing: there is one line.
    through: {},
    // None.
    walkLinks: [],
    // One line, so there is no change to make. The default is here so that the
    // table is never the thing that is missing.
    change: { default: 3 },
    // The Xplorer is the one regional line at Canberra station, and its seats
    // are booked like every other NSW TrainLink regional service: somebody
    // planning to catch it from here sees the reserved-seating note too.
    reserved: [1154],   // Sydney–Canberra
  },

  perth: {
    name: 'Perth',
    // UTC+8 all year: no daylight saving in Western Australia.
    tz: 'Australia/Perth',
    // NO BRAND NAMES, AND THAT IS THE LICENCE, NOT A STYLE. The timetable is
    // licensed on condition that the operator's trademarks — the network's
    // name, its regional coach and rail brand, its smartcard — are not used in
    // association with the data. So everywhere this app would say "Sydney
    // Trains" or "Adelaide Metro", Perth says what the trains are.
    operator: 'Perth trains',
    covers: ['Suburban trains', 'Regional trains'],
    source: 'the published Perth timetable',
    sourceShort: 'Perth’s timetable',
    apiCity: 'perth',
    // The licence on the download page asks for exactly this statement in
    // anything built on the data. The address is the one place the network's
    // name appears, because the licence itself names it.
    licence: 'Perth train timetable data is available free of charge from '
      + 'www.transperth.wa.gov.au and is used under the licence published there.',
    disclaimer: 'Late Again is not affiliated with, endorsed by or associated with '
      + 'the Western Australian Government or any operator of Perth trains.',
    // Like Canberra, nothing — and unlike Canberra, nothing live either. The
    // operator posts service updates on its own website; none of it is open
    // data, and none of it is scraped here.
    hasAlerts: false,
    live: false,
    mapUrl: 'https://www.transperth.wa.gov.au/Journey-Planner/Network-Maps',
    disruptionsUrl: 'https://www.transperth.wa.gov.au/service-updates',
    centre: { lat: -31.9512, lng: 115.8601 },   // Perth station
    defaultStopId: 56,   // Perth
    suggested: ['Perth', 'Perth Underground', 'Elizabeth Quay', 'Claisebrook'],
    // The surface station, the two underground ones the northern and southern
    // lines run through, and the first stop out on the eastern side, where the
    // Armadale, Midland and Airport lines part. City West is the western
    // equivalent and is left out because only two lines pass it.
    cityEnd: ['Perth', 'Perth Underground', 'Elizabeth Quay', 'McIver', 'Claisebrook'],
    // EMPTY, AND HONESTLY SO — now measured rather than assumed. Perth has no
    // route codes to read, so `deriveThroughByContinuity` (in the build script)
    // looks for the join itself: one line's trips ending on a platform where
    // another line's start a few minutes later, for most of that line's trips.
    // Held against the October 2026 timetable it finds none for Mandurah and
    // Yanchep. Mandurah trains end on Perth Underground platform 1 and the same
    // line's next train leaves platform 2; Yanchep trains turn at Elizabeth
    // Quay (a few at Perth Underground). No Mandurah trip is followed by a
    // Yanchep one on the platform it ended on, so the
    // timetable files two lines that meet under the city, not one that
    // continues, and drawing the continuation would assert what it does not
    // say. If the timetable ever does join them, the bundle will say so by
    // itself.
    through: {},
    // Perth and Perth Underground: one interchange, two stop ids, and a walk
    // along the concourse between the surface platforms and the ones under
    // Wellington Street.
    walkLinks: [
      [56, 64],   // Perth ↔ Perth Underground
    ],
    // East Perth's regional platform is the terminal building, not the
    // suburban island. Cockburn Central's Thornlie-Cockburn platform is the
    // third face, beside the Mandurah pair. Perth's surface platforms are a
    // footbridge apart.
    change: {
      default: 3,
      stations: {
        Perth: 4,
        'East Perth': 5,
        'Cockburn Central': 4,
        Bayswater: 3,
      },
    },
    // Booked rather than boarded: the regional railcars' own timetable says
    // reservations are essential on every service except the AvonLink.
    reserved: [
      5051,   // Australind
      5052,   // Prospector
      5053,   // MerredinLink
    ],
  },
};

/** Every city id, in the order the switcher lists them. */
export const CITY_IDS = Object.keys(CITIES);

export function isCity(id) {
  return Object.prototype.hasOwnProperty.call(CITIES, String(id));
}

/**
 * The chosen city, read once and then held.
 *
 * The in-memory copy is not an optimisation. `prefs` is a proxy over
 * localStorage and there is no localStorage under Node — the test runner, and
 * `server/push.mjs` if it ever reads this — so a `setCity` that only wrote
 * through would be a `setCity` that did nothing at all outside a browser, and
 * every test of a Sydney network would silently assert against Melbourne.
 */
let current = read();

function read() {
  if (IS_DEMO) return DEFAULT_CITY;
  try {
    const raw = localStorage.getItem(KEY);
    const id = raw === null ? null : JSON.parse(raw);
    return isCity(id) ? String(id) : DEFAULT_CITY;
  } catch {
    // Private mode, Node, a corrupt value. All three mean the same thing.
    return DEFAULT_CITY;
  }
}

/** The city id in force. Never anything but a key of `CITIES`. */
export function currentCity() {
  return current;
}

/** Everything known about the city in force. */
export function city(id = current) {
  return CITIES[isCity(id) ? String(id) : DEFAULT_CITY];
}

/**
 * Choose a city. Returns the id actually in force.
 *
 * It writes and it returns; it does NOT clear anything. What a switch costs —
 * the watched lines, the saved stations, the rules — is decided in
 * `app-state.js`, where those things live, because a person has to be asked
 * before it happens and this file cannot ask anybody anything.
 */
export function setCity(id) {
  if (!isCity(id)) return current;
  current = String(id);
  if (!IS_DEMO) {
    try { localStorage.setItem(KEY, JSON.stringify(current)); } catch { /* private mode */ }
  }
  return current;
}

/** `&city=sydney`, or nothing at all for the city the endpoints already assume. */
export function cityQuery(id = current) {
  const apiCity = city(id).apiCity;
  return apiCity ? `&city=${encodeURIComponent(apiCity)}` : '';
}

/** The same, for a URL that has no query string yet. */
export function cityFirstQuery(id = current) {
  const apiCity = city(id).apiCity;
  return apiCity ? `?city=${encodeURIComponent(apiCity)}` : '';
}

/**
 * How far a fix may be from a city's centre and still be that city.
 *
 * 300km, which is far wider than any of these networks and deliberately so.
 * The question this answers is not "which platform are you on" — the stations
 * answer that — but "which of six networks is the one you care about", and a
 * person on a train to Geelong or Newcastle still wants Melbourne or Sydney.
 * Past it the honest answer is none of them: somebody in Darwin or Auckland is
 * not served by this app at all, and moving them to whichever capital happens
 * to be least far away would be the app inventing a fact about them.
 */
const NEAR_CITY_METRES = 300_000;

const EARTH_METRES = 6_371_000;

/**
 * Which city's network is closest to a coordinate, or null if none is close.
 *
 * PURE, and the reason it is here rather than in `network.js` is that it must
 * answer BEFORE a bundle is chosen — that is the whole point of it — and
 * `network.js` can only measure against the bundle already loaded. One
 * representative coordinate per city is enough for a question whose answers
 * are six hundred kilometres apart; loading five bundles to be more precise
 * about a decision that is never close would be work for no accuracy.
 *
 * Its own haversine, because this file imports nothing (see the top) and the
 * copy in `network.js` would drag the bundles in behind it.
 */
export function nearestCity(lat, lng) {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  const rad = (deg) => (deg * Math.PI) / 180;
  let best = null;
  let bestMetres = Infinity;
  for (const id of CITY_IDS) {
    const c = CITIES[id].centre;
    if (!c) continue;
    const dLat = rad(c.lat - lat);
    const dLng = rad(c.lng - lng);
    const h = Math.sin(dLat / 2) ** 2
      + Math.cos(rad(lat)) * Math.cos(rad(c.lat)) * Math.sin(dLng / 2) ** 2;
    const metres = 2 * EARTH_METRES * Math.asin(Math.min(1, Math.sqrt(h)));
    if (metres < bestMetres) { bestMetres = metres; best = id; }
  }
  return bestMetres <= NEAR_CITY_METRES ? best : null;
}

/** Test seam: forget what was read at module load and read it again. */
export function reloadCity() {
  current = read();
  return current;
}
